import { BizResult, Ok } from "@/models/biz_model"
import { KCSDK, Resolution } from "@/sdk"
import { store } from "@/store"
import {
  setResolutionList,
  setAgentResolutionItem,
} from "@/stores/remote_desk_state_slice"
import { ScaleRatioConfigCache } from "@/utils/storage"

// 支持分辨率的最大宽度
const maxWidth = 2560
function updateAgentResolutionItem(resolution: Resolution) {
  store.dispatch(
    setAgentResolutionItem({
      label: `${resolution.width} x ${resolution.height}`,
      value: resolution,
    })
  )
}

export class DisplayManager {
  // 设备支持的分辨率，按照宽度分组
  private readonly resolutions: Resolution[] = []
  private readonly ratios: number[] = []

  constructor(resolutions: Resolution[]) {
    // 将设备支持的分辨率按照从大到小排序
    this.resolutions = resolutions
      .filter((r) => {
        return r.width <= maxWidth
      })
      .sort((a, b) => {
        if (a.width !== b.width) {
          return b.width - a.width
        } else {
          return b.height - a.height
        }
      })
    this.ratios = this.resolutions.map((r) => r.width / r.height)
  }

  public getResolutions(): Resolution[] {
    return this.resolutions
  }

  // 获取适配的分辨率
  getFitedSize({ width, height }: Resolution): Resolution {
    if (this.resolutions.length === 0) {
      // 设备未配置支持的分辨率, 返回原始分辨率
      return { width, height }
    }

    // 在支持的分辨率列表中找出最接近指定宽高的位置
    let start = 0
    let end = this.resolutions.length - 1
    // if (this.resolutions[start].width < width) {
    //   return this.resolutions[start]
    // }
    // if (this.resolutions[end].width > width) {
    //   return this.resolutions[end]
    // }

    while (start < end - 1) {
      const m = Math.floor((start + end) / 2)
      const rm = this.resolutions[m]
      if (rm.width > width) {
        start = m
      } else if (rm.width === width) {
        if (rm.height === height) {
          return rm
        }
        if (rm.height < height) {
          end = m
        } else {
          start = m
        }
      } else {
        end = m
      }
    }

    // 向上寻找的起点
    const up = this.resolutions[start]
    if (up.width === width && up.height === height) {
      return up
    }
    // 向下寻找的起点
    const down = this.resolutions[end]
    if (down.width === width && down.height === height) {
      return down
    }

    // 指定宽高比
    const specificRatio = width / height
    // 最匹配的设备支持的分辨率
    let fitted = up
    // 最小宽高比差值
    let minRatioDiff = Number.MAX_VALUE
    // 最小宽度差值
    let minWidthDiff = Number.MAX_VALUE

    const findFitted = (i: number) => {
      const r = this.resolutions[i]
      const ratio = this.ratios[i]
      const ratioDiff = Math.abs(ratio - specificRatio)
      if (ratioDiff < minRatioDiff) {
        fitted = r
        minRatioDiff = ratioDiff
        minWidthDiff = Math.abs(r.width - width)
      } else if (ratioDiff === minRatioDiff) {
        const widthDiff = Math.abs(r.width - width)
        if (widthDiff <= minWidthDiff) {
          fitted = r
          minWidthDiff = widthDiff
        }
      }
    }

    // 向下寻找合适的分辨率
    for (let i = end; i < this.resolutions.length; i++) {
      const r = this.resolutions[i]
      if (r.width < down.width) {
        break
      }
      findFitted(i)
      if (r.height < height) {
        break
      }
    }
    // 向上寻找合适的分辨率
    for (let i = start; i >= 0; i--) {
      const r = this.resolutions[i]
      if (r.width > up.width) {
        break
      }
      findFitted(i)
      if (r.height > height) {
        break
      }
    }
    return fitted
  }

  // 设置远端设备分辨率
  setRemoteStreamResolution(resolution: Resolution): void {
    const fitted = this.getFitedSize(resolution)
    KCSDK.setVideoResolution(fitted)
    updateAgentResolutionItem(fitted)
  }
}

// 等待设置的分辨率
let pendingResolution: Resolution | null = null
let manager: DisplayManager | null = null
let scaleRatioRequestVersion = 0
let pendingScaleRatioRequest: {
  resolutionKey: string
  promise: Promise<DisplayScaleRatioConfig | null>
} | null = null

// 获取到远端设备支持的分辨率列表
function onGetDisplayInfo(resolutions: Resolution[]) {
  manager = new DisplayManager(resolutions)
  const recommendedResolution = manager.getFitedSize({
    width: Math.round(window.innerWidth * window.devicePixelRatio),
    height: Math.round(window.innerHeight * window.devicePixelRatio),
  })

  const resolutionSelectOption = manager.getResolutions().map((item) => {
    const isRecommended =
      item.width === recommendedResolution.width &&
      item.height === recommendedResolution.height
    return {
      label: `${item.width} x ${item.height}${isRecommended ? " (推荐)" : ""}`,
      value: item,
    }
  })
  store.dispatch(setResolutionList(resolutionSelectOption))

  if (pendingResolution !== null) {
    // 存在等待设置的分辨率
    manager.setRemoteStreamResolution(pendingResolution)
    pendingResolution = null
  }
}

export interface DisplayScaleRatioConfig {
  selected: number
  recommended: number
  scaleRatios: number[]
}

function getResolutionKey(resolution: Resolution): string {
  return `${resolution.width}x${resolution.height}`
}

async function loadScaleRatioConfig(
  resolution: Resolution,
  requestVersion: number
): Promise<DisplayScaleRatioConfig | null> {
  if (resolution.width <= 0 || resolution.height <= 0) return null
  const screenIndex = 0
  const user = store.getState().login.currentUser?.account
  const cachedConfig =
    user === undefined
      ? null
      : ScaleRatioConfigCache.getScaleRatioConfig(user, screenIndex, resolution)
  const latestConfig = await KCSDK.getScaleRatioConfig(screenIndex)
  if (requestVersion !== scaleRatioRequestVersion) return null

  const scaleRatios = latestConfig?.scaleRatios ?? cachedConfig?.scaleRatios
  const recommended = latestConfig?.recommended ?? cachedConfig?.recommended
  const expected =
    cachedConfig?.selected ?? cachedConfig?.recommended ?? recommended
  if (
    scaleRatios === undefined ||
    recommended === undefined ||
    expected === undefined
  ) {
    return null
  }

  if (user !== undefined && latestConfig !== null) {
    ScaleRatioConfigCache.saveScaleRatioConfig({
      user,
      screenIndex,
      resolution,
      recommended,
      scaleRatios,
    })
  }
  if (latestConfig?.current !== expected) {
    void KCSDK.setScaleRatio(expected, screenIndex)
  }
  return {
    selected: expected,
    recommended,
    scaleRatios,
  }
}

async function getScaleRatioConfig(
  resolution: Resolution
): Promise<DisplayScaleRatioConfig | null> {
  const resolutionKey = getResolutionKey(resolution)
  if (pendingScaleRatioRequest?.resolutionKey === resolutionKey) {
    return await pendingScaleRatioRequest.promise
  }

  const requestVersion = ++scaleRatioRequestVersion
  const promise = loadScaleRatioConfig(resolution, requestVersion)
  pendingScaleRatioRequest = { resolutionKey, promise }
  try {
    return await promise
  } finally {
    if (pendingScaleRatioRequest?.promise === promise) {
      pendingScaleRatioRequest = null
    }
  }
}

async function setScaleRatio(
  scaleRatio: number,
  resolution: Resolution
): Promise<boolean> {
  if (resolution.width <= 0 || resolution.height <= 0) return false
  const screenIndex = 0
  const isSuccess = await KCSDK.setScaleRatio(scaleRatio, screenIndex)
  const user = store.getState().login.currentUser?.account
  if (isSuccess && user !== undefined) {
    ScaleRatioConfigCache.saveScaleRatioConfig({
      user,
      screenIndex,
      resolution,
      selected: scaleRatio,
    })
  }
  return isSuccess
}

function onGetAgentResolution(resolution: Resolution) {
  updateAgentResolutionItem(resolution)
}

/**
 * 设置远端视频流分辨率
 */
function setRemoteStreamResolution(
  width: number,
  height: number
): BizResult<number> {
  if (manager === null) {
    pendingResolution = { width, height }
  } else {
    manager.setRemoteStreamResolution({ width, height })
  }
  return new Ok(0)
}

function onDisconnect() {
  pendingResolution = null
  manager = null
  scaleRatioRequestVersion += 1
  pendingScaleRatioRequest = null
  store.dispatch(setAgentResolutionItem(null))
}

export const DisplayUseCase = {
  onGetDisplayInfo,
  onGetAgentResolution,
  setRemoteStreamResolution,
  getScaleRatioConfig,
  setScaleRatio,
  onDisconnect,
}
